import './FormField.css';
import { useState, useEffect } from 'react';

interface FormFieldProps {
  label: string;
  name: string;
  type?: 'text' | 'email' | 'number' | 'password' | 'date' | 'textarea' | 'select';
  value: string | number;
  onChange: (value: string | number) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  options?: Array<{ value: string | number; label: string }>;
  rows?: number;
  validateOnChange?: boolean;
  validator?: (value: string | number) => string | null;
  helpText?: string;
  className?: string;
}

export default function FormField({
  label,
  name,
  type = 'text',
  value,
  onChange,
  error,
  placeholder,
  required = false,
  disabled = false,
  min,
  max,
  step,
  options,
  rows = 4,
  validateOnChange = true,
  validator,
  helpText,
  className = '',
}: FormFieldProps) {
  const [localError, setLocalError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (validateOnChange && touched && validator) {
      const validationError = validator(value);
      setLocalError(validationError || null);
    }
  }, [value, touched, validator, validateOnChange]);

  const handleBlur = () => {
    setTouched(true);
    if (validator) {
      const validationError = validator(value);
      setLocalError(validationError || null);
    }
  };

  const displayError = error || localError;
  const hasError = !!displayError;

  const inputId = `field-${name}`;

  return (
    <div className={`form-field ${hasError ? 'form-field-error' : ''} ${className}`}>
      <label htmlFor={inputId} className="form-field-label">
        {label}
        {required && <span className="form-field-required">*</span>}
      </label>
      
      {type === 'textarea' ? (
        <textarea
          id={inputId}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          rows={rows}
          className={`form-field-input form-field-textarea ${hasError ? 'form-field-input-error' : ''}`}
        />
      ) : type === 'select' ? (
        <select
          id={inputId}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={handleBlur}
          required={required}
          disabled={disabled}
          className={`form-field-input form-field-select ${hasError ? 'form-field-input-error' : ''}`}
        >
          <option value="">{placeholder || 'Seleccionar...'}</option>
          {options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={inputId}
          name={name}
          type={type}
          value={value}
          onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          className={`form-field-input ${hasError ? 'form-field-input-error' : ''}`}
        />
      )}

      {helpText && !hasError && (
        <div className="form-field-help">{helpText}</div>
      )}

      {hasError && (
        <div className="form-field-error-message">
          <span className="form-field-error-icon">⚠️</span>
          {displayError}
        </div>
      )}
    </div>
  );
}
